import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  ArrowDown,
  ArrowUp,
  Copy,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Monitor,
  Plus,
  Save,
  Smartphone,
  Trash2,
  X,
  Pencil,
  GripVertical,
  Settings,
  Type,
  Palette,
  Layout,
  ChevronDown,
  ChevronUp,
  Layers,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { landingPagesApi, productsApi } from "../lib/api";

// ─── Confirm Dialog (replaces window.confirm) ────────────────────────────────
const ConfirmDialog = ({ open, title, message, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onCancel}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100">
            <AlertTriangle className="h-5 w-5 text-rose-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-stone-900">{title}</h3>
            <p className="mt-1 text-sm text-stone-600">{message}</p>
          </div>
        </div>
        <div className="mt-5 flex gap-3 justify-end">
          <button onClick={onCancel} className="h-10 px-4 rounded-xl border border-stone-200 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            Batal
          </button>
          <button onClick={onConfirm} className="h-10 px-4 rounded-xl bg-rose-600 text-sm font-medium text-white hover:bg-rose-700 transition-colors">
            Hapus
          </button>
        </div>
      </div>
    </div>
  );
};

const slugify = (value = "") =>
  value.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const defaultPage = {
  template_id: "",
  product_id: "",
  slug: "",
  title: "",
  description: "",
  status: "draft",
  is_active: true,
  seo_title: "",
  seo_description: "",
  og_image: "",
  cta_config: {
    mode: "both",
    whatsapp_number: "",
    whatsapp_text: "Halo saya tertarik dengan produk ini",
    custom_url: "",
  },
  sections: [],
};

const SECTION_TYPES = {
  hero: { label: "Hero", icon: "🎯", desc: "Banner utama dengan headline & gambar produk" },
  problem: { label: "Problem", icon: "⚡", desc: "Masalah yang dipecahkan produk" },
  solution: { label: "Solusi", icon: "💡", desc: "Solusi produk dengan gambar & poin-poin" },
  benefit: { label: "Benefit", icon: "✨", desc: "Keunggulan & manfaat produk" },
  about_product: { label: "Mengenal", icon: "💎", desc: "Perkenalan produk dengan background gradient" },
  ingredients: { label: "Ingredients", icon: "🌿", desc: "Komposisi & bahan produk" },
  how_to_use: { label: "Cara Pakai", icon: "📋", desc: "Langkah-langkah pemakaian" },
  testimonial: { label: "Testimonial", icon: "💬", desc: "Review & testimoni pelanggan" },
  offer: { label: "Penawaran", icon: "🎟️", desc: "Promo dengan kupon, countdown & harga coret" },
  hard_sell: { label: "Hard Sell", icon: "🔥", desc: "Kutipan persuasif dengan bullet point" },
  urgency: { label: "Urgency", icon: "⏳", desc: "Stok terbatas dengan progress bar" },
  guarantee: { label: "Garansi", icon: "🛡️", desc: "Trust badges & garansi pembelian" },
  final_cta: { label: "Final CTA", icon: "🚀", desc: "CTA terakhir dengan kupon & harga" },
  faq: { label: "FAQ", icon: "❓", desc: "Pertanyaan yang sering ditanyakan" },
  cta: { label: "CTA", icon: "🛒", desc: "Tombol aksi checkout/WhatsApp" },
  stats: { label: "Statistik", icon: "📊", desc: "Angka-angka penting" },
  custom: { label: "Custom", icon: "🧩", desc: "Section kustom" },
};

const sectionLabel = (type) => SECTION_TYPES[type]?.label || type;
const sectionIcon = (type) => SECTION_TYPES[type]?.icon || "📄";

const toUiSection = (s) => {
  const data = s.config_json || {};
  return {
    section_type: s.section_type,
    section_order: s.section_order,
    is_enabled: s.is_enabled,
    image_source: s.image_source || "product",
    image_url: s.image_url || "",
    content: data.content || data || {},
    style: data.style || {},
  };
};

const toApiSection = (s) => ({
  section_type: s.section_type,
  section_order: s.section_order,
  is_enabled: s.is_enabled,
  image_source: s.image_source || "product",
  image_url: s.image_url || "",
  config_json: {
    content: s.content || {},
    style: s.style || {},
  },
});

// ─── Inline Editable Text ────────────────────────────────────────────────────
const EditableText = ({ value, onChange, tag: Tag = "p", className = "", placeholder = "Klik untuk edit...", multiline = true }) => {
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);

  const handleBlur = () => {
    setEditing(false);
    // Get text with newlines preserved (from <br> and block elements)
    const html = ref.current?.innerHTML || "";
    const newVal = html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/div><div>/gi, "\n")
      .replace(/<\/p><p>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .trim();
    if (newVal !== (value || "")) onChange(newVal);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (multiline && e.shiftKey) {
        // Shift+Enter = newline (allow default browser behavior for contentEditable)
        return;
      }
      // Enter without shift = save & close
      e.preventDefault();
      ref.current?.blur();
    }
  };

  // Convert newlines to <br> for display in contentEditable
  const displayHtml = (value || placeholder || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");

  return (
    <Tag
      ref={ref}
      contentEditable={editing}
      suppressContentEditableWarning
      onClick={() => setEditing(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`outline-none cursor-text transition-all whitespace-pre-wrap ${editing ? "ring-2 ring-emerald-400 rounded-lg px-1 bg-white/90" : "hover:ring-2 hover:ring-emerald-200 hover:rounded-lg"} ${className} ${!value ? "text-stone-400 italic" : ""}`}
      data-placeholder={placeholder}
      dangerouslySetInnerHTML={{ __html: value ? displayHtml : placeholder }}
    />
  );
};

// ─── Inline Editable Image ───────────────────────────────────────────────────
const EditableImage = ({ src, onChangeUrl, className = "", alt = "" }) => {
  const [showInput, setShowInput] = useState(false);
  const [url, setUrl] = useState(src || "");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { landingPagesApi } = await import("../lib/api");
      const result = await landingPagesApi.uploadImage(file);
      if (result?.url) {
        setUrl(result.url);
        onChangeUrl(result.url);
        setShowInput(false);
      }
    } catch (err) {
      toast.error(err.message || "Gagal upload gambar");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`relative group ${className}`}>
      {src ? (
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-stone-100 text-stone-400">
          <ImageIcon className="w-8 h-8" />
        </div>
      )}
      {/* Overlay edit button */}
      <button
        onClick={() => setShowInput(true)}
        className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors"
      >
        <span className="hidden group-hover:flex items-center gap-2 bg-white rounded-xl px-4 py-2 text-sm font-medium text-stone-700 shadow-lg">
          <ImageIcon className="w-4 h-4" /> Ganti Gambar
        </span>
      </button>
      {/* Upload/URL modal */}
      {showInput && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 p-4" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-xl">
            <p className="text-sm font-semibold text-stone-800 mb-3">Ganti Gambar</p>
            {/* Upload file */}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full h-10 mb-3 rounded-lg border-2 border-dashed border-stone-300 text-sm text-stone-600 hover:border-emerald-400 hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {uploading ? (
                <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600" /> Uploading...</>
              ) : (
                <><Plus className="w-4 h-4" /> Upload dari komputer</>
              )}
            </button>
            {/* URL input */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-px bg-stone-200" />
              <span className="text-xs text-stone-400">atau</span>
              <div className="flex-1 h-px bg-stone-200" />
            </div>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="w-full h-10 px-3 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => { onChangeUrl(url); setShowInput(false); }}
                className="flex-1 h-9 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
              >
                Simpan URL
              </button>
              <button
                onClick={() => setShowInput(false)}
                className="flex-1 h-9 border border-stone-200 rounded-lg text-sm font-medium hover:bg-stone-50"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Visual Section Renderers ────────────────────────────────────────────────
const SectionRenderer = ({ section, product, onUpdate, isActive, onClick, onDelete, onMoveUp, onMoveDown, onDuplicate, onToggle }) => {
  const content = section.content || {};
  const style = section.style || {};
  const bg = style.bg || "#ffffff";
  const accent = style.accent || "#059669";
  const image = section.image_source === "url" ? section.image_url : (section.image_source === "product" ? product?.image : section.image_url);

  const updateContent = (key, value) => {
    onUpdate({ content: { ...content, [key]: value } });
  };

  const updateImage = (url) => {
    onUpdate({ image_source: "url", image_url: url });
  };

  // Section toolbar
  const Toolbar = () => (
    <div className={`absolute top-2 right-2 z-20 flex items-center gap-1 rounded-xl bg-white border border-stone-200 p-1 shadow-lg transition-opacity ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
      <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} className="p-1.5 rounded-lg hover:bg-stone-100" title="Naik"><ArrowUp className="w-3.5 h-3.5" /></button>
      <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} className="p-1.5 rounded-lg hover:bg-stone-100" title="Turun"><ArrowDown className="w-3.5 h-3.5" /></button>
      <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }} className="p-1.5 rounded-lg hover:bg-stone-100" title="Duplikat"><Copy className="w-3.5 h-3.5" /></button>
      <button onClick={(e) => { e.stopPropagation(); onToggle(); }} className="p-1.5 rounded-lg hover:bg-stone-100" title="Sembunyikan">
        {section.is_enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-stone-400" />}
      </button>
      <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1.5 rounded-lg hover:bg-rose-100 text-rose-600" title="Hapus"><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );

  // Section type badge
  const TypeBadge = () => (
    <div className={`absolute top-2 left-2 z-20 flex items-center gap-1.5 rounded-lg bg-white/90 border border-stone-200 px-2 py-1 text-xs font-medium text-stone-600 backdrop-blur transition-opacity ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
      <span>{sectionIcon(section.section_type)}</span>
      {sectionLabel(section.section_type)}
    </div>
  );

  const wrapperClass = `group relative rounded-2xl border-2 transition-all cursor-pointer overflow-hidden ${
    isActive ? "border-emerald-500 shadow-lg shadow-emerald-100" : "border-transparent hover:border-emerald-200"
  } ${!section.is_enabled ? "opacity-40 grayscale" : ""}`;

  // ── HERO ──
  if (section.section_type === "hero") {
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="grid md:grid-cols-2 gap-6 p-8">
          <div className="space-y-4">
            <EditableText
              value={content.title}
              onChange={(v) => updateContent("title", v)}
              tag="h1"
              className="text-3xl font-bold text-stone-900"
              placeholder="Headline produk Anda..."
            />
            <EditableText
              value={content.desc}
              onChange={(v) => updateContent("desc", v)}
              tag="p"
              className="text-base text-stone-600 leading-relaxed"
              placeholder="Subheadline yang menarik..."
            />
            {content.bullets && (
              <div className="space-y-2">
                {(Array.isArray(content.bullets) ? content.bullets : []).map((b, i) => (
                  <div key={i} className="flex items-center gap-2 group/item">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-xs">✓</span>
                    <EditableText
                      value={b}
                      onChange={(v) => {
                        const bullets = [...(content.bullets || [])];
                        bullets[i] = v;
                        updateContent("bullets", bullets);
                      }}
                      tag="span"
                      className="text-sm text-stone-700 flex-1"
                      placeholder="Poin benefit..."
                    />
                    <button onClick={(e) => { e.stopPropagation(); const arr = (content.bullets || []).filter((_, idx) => idx !== i); updateContent("bullets", arr); }} className="hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow shrink-0" title="Hapus">×</button>
                  </div>
                ))}
                <button onClick={(e) => { e.stopPropagation(); updateContent("bullets", [...(content.bullets || []), ""]); }} className="flex items-center gap-2 text-xs text-stone-500 hover:text-emerald-600 transition-colors mt-1">
                  <Plus className="w-3.5 h-3.5" /> Tambah bullet
                </button>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button className="h-12 px-6 rounded-xl text-white text-sm font-medium" style={{ background: accent }}>
                {content.cta_text || "Checkout Sekarang"}
              </button>
            </div>
          </div>
          <EditableImage
            src={image}
            onChangeUrl={updateImage}
            className="aspect-[4/3] rounded-2xl overflow-hidden"
            alt={content.title || "Hero"}
          />
        </div>
      </div>
    );
  }

  // ── PROBLEM ──
  if (section.section_type === "problem") {
    const items = content.items || [];
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-8">
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold text-stone-900 text-center" placeholder="Masalah yang dipecahkan..." />
          <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="text-stone-600 text-center mt-2" placeholder="Deskripsi masalah..." />
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {items.map((item, i) => (
              <div key={i} className="relative p-4 rounded-xl border border-stone-200 bg-white group/item">
                <button onClick={(e) => { e.stopPropagation(); const arr = items.filter((_, idx) => idx !== i); updateContent("items", arr); }} className="absolute -top-2 -right-2 z-10 hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow" title="Hapus item">×</button>
                <div className="flex items-start gap-3">
                  {item.icon && <EditableText value={item.icon} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], icon: v }; updateContent("items", arr); }} tag="span" className="text-2xl" placeholder="😩" />}
                  <div className="flex-1">
                    <EditableText value={item.title} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], title: v }; updateContent("items", arr); }} tag="h3" className="font-semibold text-stone-900" placeholder="Judul masalah..." />
                    <EditableText value={item.desc} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], desc: v }; updateContent("items", arr); }} tag="p" className="text-sm text-stone-600 mt-1" placeholder="Deskripsi..." />
                  </div>
                </div>
              </div>
            ))}
            <button onClick={(e) => { e.stopPropagation(); updateContent("items", [...items, { title: "", desc: "", icon: "😩" }]); }} className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
              <Plus className="w-4 h-4" /> Tambah Item
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── BENEFIT ──
  if (section.section_type === "benefit") {
    const items = content.items || [];
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-8">
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold text-stone-900 text-center" placeholder="Keunggulan produk..." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {items.map((item, i) => {
              const isObj = typeof item === "object" && item !== null;
              return (
                <div key={i} className="relative flex flex-col items-start gap-2 p-4 rounded-xl border border-stone-100 group/item">
                  <button onClick={(e) => { e.stopPropagation(); const arr = items.filter((_, idx) => idx !== i); updateContent("items", arr); }} className="absolute -top-2 -right-2 z-10 hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow" title="Hapus item">×</button>
                  {isObj && item.icon !== undefined ? (
                    <>
                      <EditableText value={item.icon} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], icon: v }; updateContent("items", arr); }} tag="span" className="text-2xl" placeholder="✨" />
                      <EditableText value={item.title || item.text} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], title: v, text: v }; updateContent("items", arr); }} tag="h3" className="font-semibold text-sm text-stone-900" placeholder="Judul benefit..." />
                      <EditableText value={item.desc} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], desc: v }; updateContent("items", arr); }} tag="p" className="text-xs text-stone-600" placeholder="Deskripsi..." />
                    </>
                  ) : (
                    <div className="flex items-start gap-3 w-full">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: accent }}>✓</span>
                      <EditableText value={typeof item === "string" ? item : item.text || item.title} onChange={(v) => { const arr = [...items]; arr[i] = typeof item === "string" ? v : { ...arr[i], text: v, title: v }; updateContent("items", arr); }} tag="p" className="text-sm text-stone-700 flex-1" placeholder="Manfaat produk..." />
                    </div>
                  )}
                </div>
              );
            })}
            <button onClick={(e) => { e.stopPropagation(); updateContent("items", [...items, { icon: "✨", title: "", desc: "" }]); }} className="flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
              <Plus className="w-4 h-4" /> Tambah Benefit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── TESTIMONIAL ──
  if (section.section_type === "testimonial") {
    const items = content.items || [];
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-8">
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold text-stone-900 text-center" placeholder="Apa kata pelanggan..." />
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {items.map((item, i) => (
              <div key={i} className="relative p-4 rounded-xl border border-stone-200 bg-white group/item">
                <button onClick={(e) => { e.stopPropagation(); const arr = items.filter((_, idx) => idx !== i); updateContent("items", arr); }} className="absolute -top-2 -right-2 z-10 hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow" title="Hapus item">×</button>
                <div className="flex gap-0.5 text-amber-400 mb-2">{"★★★★★"}</div>
                <div className="mb-3 flex items-center gap-3">
                  <EditableImage
                    src={item.image_url}
                    onChangeUrl={(v) => { const arr = [...items]; arr[i] = { ...arr[i], image_url: v }; updateContent("items", arr); }}
                    className="h-28 w-24 overflow-hidden rounded-2xl border border-stone-200 bg-stone-50 shrink-0"
                    alt={item.name || "Lampiran testimoni"}
                  />
                  <p className="text-xs leading-5 text-stone-500">
                    Upload lampiran testimoni opsional, misalnya screenshot chat atau foto hasil pemakaian.
                  </p>
                </div>
                <EditableText value={item.text} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], text: v }; updateContent("items", arr); }} tag="p" className="text-sm text-stone-600 italic" placeholder="Testimoni..." />
                <EditableText value={item.name} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], name: v }; updateContent("items", arr); }} tag="p" className="text-sm font-semibold text-stone-900 mt-3" placeholder="Nama..." />
                <EditableText value={item.role} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], role: v }; updateContent("items", arr); }} tag="p" className="text-xs text-stone-500" placeholder="Lokasi..." />
              </div>
            ))}
            <button onClick={(e) => { e.stopPropagation(); updateContent("items", [...items, { name: "", text: "", role: "", image_url: "" }]); }} className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
              <Plus className="w-4 h-4" /> Tambah Testimoni
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── FAQ ──
  if (section.section_type === "faq") {
    const items = content.items || [];
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-8 max-w-3xl mx-auto">
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold text-stone-900 text-center" placeholder="FAQ..." />
          <div className="mt-6 space-y-3">
            {items.map((item, i) => (
              <div key={i} className="relative p-4 rounded-xl border border-stone-200 bg-white group/item">
                <button onClick={(e) => { e.stopPropagation(); const arr = items.filter((_, idx) => idx !== i); updateContent("items", arr); }} className="absolute -top-2 -right-2 z-10 hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow" title="Hapus item">×</button>
                <EditableText value={item.q} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], q: v }; updateContent("items", arr); }} tag="p" className="font-semibold text-stone-900" placeholder="Pertanyaan..." />
                <EditableText value={item.a} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], a: v }; updateContent("items", arr); }} tag="p" className="text-sm text-stone-600 mt-1" placeholder="Jawaban..." />
              </div>
            ))}
            <button onClick={(e) => { e.stopPropagation(); updateContent("items", [...items, { q: "", a: "" }]); }} className="flex items-center justify-center gap-2 w-full p-4 rounded-xl border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
              <Plus className="w-4 h-4" /> Tambah FAQ
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── HOW TO USE / STEPS ──
  if (section.section_type === "how_to_use") {
    const items = content.items || [];
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-8">
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold text-stone-900 text-center" placeholder="Cara Pakai..." />
          <div className="grid md:grid-cols-4 gap-4 mt-6">
            {items.map((item, i) => (
              <div key={i} className="relative p-4 rounded-xl border border-stone-200 bg-white text-center group/item">
                <button onClick={(e) => { e.stopPropagation(); const arr = items.filter((_, idx) => idx !== i); updateContent("items", arr); }} className="absolute -top-2 -right-2 z-10 hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow" title="Hapus item">×</button>
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: accent }}>{i + 1}</span>
                <EditableText value={item.title} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], title: v }; updateContent("items", arr); }} tag="h3" className="font-semibold text-stone-900 mt-2" placeholder="Judul step..." />
                <EditableText value={item.desc} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], desc: v }; updateContent("items", arr); }} tag="p" className="text-xs text-stone-600 mt-1" placeholder="Deskripsi..." />
              </div>
            ))}
            <button onClick={(e) => { e.stopPropagation(); updateContent("items", [...items, { title: "", desc: "" }]); }} className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
              <Plus className="w-4 h-4" /> Tambah Step
            </button>
          </div>
          <div className="mt-4">
            <EditableText value={content.tip} onChange={(v) => updateContent("tip", v)} tag="p" className="text-sm text-stone-500 bg-emerald-50 rounded-xl p-3 text-center" placeholder="💡 Tips: Tambahkan tips opsional di sini..." />
          </div>
        </div>
      </div>
    );
  }

  // ── ABOUT PRODUCT (Mengenal) ──
  if (section.section_type === "about_product") {
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: "linear-gradient(135deg, #0369a1, #0891b2)" }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-10 text-center text-white">
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold" placeholder="Mengenal Produk..." />
          <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="mt-4 text-white/90 leading-relaxed max-w-3xl mx-auto" placeholder="Deskripsi tentang produk..." />
          <EditableText value={content.subtitle} onChange={(v) => updateContent("subtitle", v)} tag="p" className="mt-3 text-white/70 text-sm max-w-2xl mx-auto" placeholder="Keterangan tambahan (opsional)..." />
        </div>
      </div>
    );
  }

  // ── CTA ──
  if (section.section_type === "cta") {
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: accent }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-8 text-center text-white">
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold" placeholder="Siap order sekarang?" />
          <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="text-white/80 mt-2" placeholder="Deskripsi CTA..." />
          <div className="flex gap-3 justify-center mt-6 flex-wrap">
            <div className="h-12 px-6 rounded-xl bg-white font-medium flex items-center" style={{ color: accent }}>
              <EditableText value={content.cta_checkout} onChange={(v) => updateContent("cta_checkout", v)} tag="span" className="text-sm font-semibold" placeholder="Checkout Sekarang" />
            </div>
            <div className="h-12 px-6 rounded-xl border-2 border-white/40 font-medium flex items-center text-white">
              <EditableText value={content.cta_whatsapp} onChange={(v) => updateContent("cta_whatsapp", v)} tag="span" className="text-sm font-semibold" placeholder="Tanya via WhatsApp" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── INGREDIENTS ──
  if (section.section_type === "ingredients") {
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="grid md:grid-cols-2 gap-6 p-8">
          <div>
            <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold text-stone-900" placeholder="Bahan & Komposisi..." />
            <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="text-stone-600 mt-2 leading-relaxed" placeholder="Deskripsi bahan..." />
          </div>
          <EditableImage
            src={image}
            onChangeUrl={updateImage}
            className="aspect-square rounded-2xl overflow-hidden"
            alt={content.title || "Ingredients"}
          />
        </div>
      </div>
    );
  }

  // ── STATS ──
  if (section.section_type === "stats") {
    const items = content.items || [];
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-8">
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold text-stone-900 text-center mb-1" placeholder="Judul statistik..." />
          <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="text-stone-600 text-center text-sm mb-6" placeholder="Deskripsi opsional..." />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {items.map((item, i) => (
              <div key={i} className="relative text-center p-4 rounded-xl border border-stone-200 group/item">
                <button onClick={(e) => { e.stopPropagation(); const arr = items.filter((_, idx) => idx !== i); updateContent("items", arr); }} className="absolute -top-2 -right-2 z-10 hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow" title="Hapus item">×</button>
                <EditableText value={item.number} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], number: v }; updateContent("items", arr); }} tag="p" className="text-3xl font-bold" style={{ color: accent }} placeholder="99" />
                <EditableText value={item.label} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], label: v }; updateContent("items", arr); }} tag="p" className="text-xs text-stone-600 mt-1" placeholder="Label..." />
              </div>
            ))}
            <button onClick={(e) => { e.stopPropagation(); updateContent("items", [...items, { number: "", label: "" }]); }} className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
              <Plus className="w-4 h-4" /> Tambah Stat
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── SOLUTION ──
  if (section.section_type === "solution") {
    const points = content.points || [];
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="grid md:grid-cols-2 gap-8 p-8 items-center">
          <EditableImage
            src={image}
            onChangeUrl={updateImage}
            className="aspect-square rounded-3xl overflow-hidden"
            alt={content.title || "Solution"}
          />
          <div className="space-y-4">
            <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold text-stone-900" placeholder="Solusi Alami..." />
            <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="text-stone-600 leading-relaxed" placeholder="Deskripsi solusi produk..." />
            <div className="space-y-2">
              {points.map((p, i) => (
                <div key={i} className="flex items-center gap-2 group/item">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-xs">✓</span>
                  <EditableText
                    value={p}
                    onChange={(v) => { const arr = [...points]; arr[i] = v; updateContent("points", arr); }}
                    tag="span"
                    className="text-sm text-stone-700 flex-1"
                    placeholder="Poin solusi..."
                  />
                  <button onClick={(e) => { e.stopPropagation(); const arr = points.filter((_, idx) => idx !== i); updateContent("points", arr); }} className="hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow shrink-0" title="Hapus">×</button>
                </div>
              ))}
              <button onClick={(e) => { e.stopPropagation(); updateContent("points", [...points, ""]); }} className="flex items-center gap-2 text-xs text-stone-500 hover:text-emerald-600 transition-colors mt-1">
                <Plus className="w-3.5 h-3.5" /> Tambah Poin
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── OFFER ──
  if (section.section_type === "offer") {
    const bonusItems = content.bonus_items || [];
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: "linear-gradient(135deg, #1e3a5f, #0e7490)" }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-8 text-white">
          <div className="text-center mb-6">
            <EditableText value={content.badge} onChange={(v) => updateContent("badge", v)} tag="span" className="inline-block rounded-full bg-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-widest" placeholder="Badge promo..." />
            <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="mt-3 text-2xl font-bold" placeholder="Promo Spesial..." />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white text-stone-900 p-6">
              <div className="flex items-end gap-3">
                <span className="text-stone-400 line-through text-lg">Harga Normal</span>
                <span className="text-3xl font-bold" style={{ color: accent }}>Harga Promo</span>
              </div>
              <div className="mt-4 space-y-2">
                {bonusItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 group/item">
                    <span className="text-emerald-600 text-sm">✓</span>
                    <EditableText
                      value={item}
                      onChange={(v) => { const arr = [...bonusItems]; arr[i] = v; updateContent("bonus_items", arr); }}
                      tag="span"
                      className="text-sm text-stone-700 flex-1"
                      placeholder="Bonus item..."
                    />
                    <button onClick={(e) => { e.stopPropagation(); const arr = bonusItems.filter((_, idx) => idx !== i); updateContent("bonus_items", arr); }} className="hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow shrink-0" title="Hapus">×</button>
                  </div>
                ))}
                <button onClick={(e) => { e.stopPropagation(); updateContent("bonus_items", [...bonusItems, ""]); }} className="flex items-center gap-2 text-xs text-stone-500 hover:text-emerald-600 transition-colors mt-1">
                  <Plus className="w-3.5 h-3.5" /> Tambah Bonus
                </button>
              </div>
              <div className="mt-4 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 p-3 text-center">
                <span className="text-xs uppercase tracking-widest text-stone-500">🎁 Kode Kupon</span>
                <EditableText value={content.coupon_code} onChange={(v) => updateContent("coupon_code", v)} tag="p" className="font-bold text-xl text-emerald-700 mt-1" placeholder="KODE20" />
                <EditableText value={content.coupon_label} onChange={(v) => updateContent("coupon_label", v)} tag="p" className="text-xs text-stone-600" placeholder="Diskon Tambahan 20%" />
              </div>
              <div className="mt-4 flex gap-3 justify-center">
                <button className="h-11 px-5 rounded-xl text-white text-sm font-medium" style={{ background: accent }}>Claim Diskon & Checkout</button>
              </div>
            </div>
            <div className="flex flex-col justify-center text-center md:text-left">
              <span className="text-sm uppercase tracking-widest text-white/80">Promo Berakhir Dalam</span>
              <div className="mt-3 flex gap-3 justify-center md:justify-start">
                {["Jam", "Menit", "Detik"].map((label) => (
                  <div key={label} className="rounded-xl bg-white/15 border border-white/30 p-3 min-w-[70px] text-center">
                    <div className="text-2xl font-bold">00</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/80">{label}</div>
                  </div>
                ))}
              </div>
              <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="mt-4 text-white/90 text-sm" placeholder="Deskripsi urgency promo..." />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── HARD SELL ──
  if (section.section_type === "hard_sell") {
    const points = content.points || [];
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="grid md:grid-cols-[1fr_1.2fr] gap-8 p-8 items-center">
          <EditableImage
            src={image}
            onChangeUrl={updateImage}
            className="aspect-square rounded-3xl overflow-hidden"
            alt="Hard Sell"
          />
          <div className="space-y-4">
            <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="blockquote" className="text-xl font-bold text-stone-900 leading-tight italic" placeholder="Kutipan persuasif..." />
            <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="text-stone-600 leading-relaxed" placeholder="Deskripsi pendukung..." />
            <div className="space-y-2">
              {points.map((p, i) => (
                <div key={i} className="flex items-center gap-2 group/item">
                  <span className="text-lg">🌺</span>
                  <EditableText
                    value={p}
                    onChange={(v) => { const arr = [...points]; arr[i] = v; updateContent("points", arr); }}
                    tag="span"
                    className="text-sm text-stone-700 flex-1"
                    placeholder="Poin selling..."
                  />
                  <button onClick={(e) => { e.stopPropagation(); const arr = points.filter((_, idx) => idx !== i); updateContent("points", arr); }} className="hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow shrink-0" title="Hapus">×</button>
                </div>
              ))}
              <button onClick={(e) => { e.stopPropagation(); updateContent("points", [...points, ""]); }} className="flex items-center gap-2 text-xs text-stone-500 hover:text-emerald-600 transition-colors mt-1">
                <Plus className="w-3.5 h-3.5" /> Tambah Poin
              </button>
            </div>
            <div className="pt-2">
              <button className="h-11 px-5 rounded-xl text-white text-sm font-medium" style={{ background: accent }}>
                <EditableText value={content.cta_text} onChange={(v) => updateContent("cta_text", v)} tag="span" className="text-white font-medium" placeholder="YA, Saya Mau!" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── URGENCY ──
  if (section.section_type === "urgency") {
    const remaining = content.remaining || 27;
    const total = content.total || 100;
    const pct = (remaining / total) * 100;
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-8 max-w-3xl mx-auto text-center">
          <span className="inline-block rounded-full bg-rose-50 text-rose-600 px-4 py-1.5 text-xs font-bold uppercase tracking-widest">⚠ Stok Hampir Habis</span>
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="mt-3 text-2xl font-bold text-stone-900" placeholder="Stok Promo Hampir Habis..." />
          <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="mt-2 text-stone-600" placeholder="Deskripsi urgency..." />
          <div className="mt-6 rounded-2xl bg-white border border-stone-200 p-5">
            <div className="flex justify-between text-sm font-semibold">
              <span style={{ color: accent }}>Tersisa {remaining} Paket</span>
              <span className="text-stone-500">dari {total}</span>
            </div>
            <div className="mt-3 h-4 rounded-full bg-stone-100 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: accent }} />
            </div>
            <div className="flex gap-4 mt-4 justify-center">
              <div>
                <label className="text-xs text-stone-500">Tersisa</label>
                <input type="number" value={remaining} onClick={(e) => e.stopPropagation()} onChange={(e) => updateContent("remaining", parseInt(e.target.value) || 0)} className="w-20 h-8 px-2 text-center text-sm rounded-lg border border-stone-200" />
              </div>
              <div>
                <label className="text-xs text-stone-500">Total</label>
                <input type="number" value={total} onClick={(e) => e.stopPropagation()} onChange={(e) => updateContent("total", parseInt(e.target.value) || 100)} className="w-20 h-8 px-2 text-center text-sm rounded-lg border border-stone-200" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── GUARANTEE ──
  if (section.section_type === "guarantee") {
    const items = content.items || [];
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-8">
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold text-stone-900 text-center" placeholder="Kenapa Harus Pesan Hari Ini?" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
            {items.map((item, i) => (
              <div key={i} className="relative rounded-xl border border-stone-200 bg-white p-4 text-center group/item">
                <button onClick={(e) => { e.stopPropagation(); const arr = items.filter((_, idx) => idx !== i); updateContent("items", arr); }} className="absolute -top-2 -right-2 z-10 hidden group-hover/item:flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white text-xs shadow" title="Hapus item">×</button>
                <EditableText value={item.icon} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], icon: v }; updateContent("items", arr); }} tag="div" className="text-3xl" placeholder="🔒" />
                <EditableText value={item.title} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], title: v }; updateContent("items", arr); }} tag="h3" className="font-semibold text-stone-900 mt-2 text-sm" placeholder="Judul..." />
                <EditableText value={item.desc} onChange={(v) => { const arr = [...items]; arr[i] = { ...arr[i], desc: v }; updateContent("items", arr); }} tag="p" className="text-xs text-stone-500 mt-1" placeholder="Deskripsi..." />
              </div>
            ))}
            <button onClick={(e) => { e.stopPropagation(); updateContent("items", [...items, { icon: "🎁", title: "", desc: "" }]); }} className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
              <Plus className="w-4 h-4" /> Tambah Item
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── FINAL CTA ──
  if (section.section_type === "final_cta") {
    return (
      <div className={wrapperClass} onClick={onClick} style={{ background: "linear-gradient(135deg, #1e3a5f, #0e7490)" }}>
        <TypeBadge />
        <Toolbar />
        <div className="p-10 text-center text-white">
          <EditableText value={content.badge} onChange={(v) => updateContent("badge", v)} tag="span" className="inline-block rounded-full bg-white/15 border border-white/30 px-4 py-1.5 text-xs font-bold uppercase tracking-widest" placeholder="🌺 Penawaran Terakhir" />
          <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="mt-4 text-3xl font-bold" placeholder="Mulai Kebiasaan Sehat Hari Ini" />
          <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="mt-3 text-white/80 max-w-xl mx-auto" placeholder="Deskripsi penawaran terakhir..." />
          <div className="mt-6 inline-block rounded-2xl bg-white text-stone-900 p-6 shadow-xl">
            <span className="text-xs uppercase tracking-widest text-stone-500">Harga Promo Hari Ini</span>
            <div className="text-4xl font-bold mt-1" style={{ color: accent }}>Harga Produk</div>
            <div className="mt-4 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 px-4 py-2">
              <EditableText value={content.coupon_label} onChange={(v) => updateContent("coupon_label", v)} tag="span" className="text-[10px] uppercase tracking-widest text-stone-500" placeholder="Kode Diskon" />
              <EditableText value={content.coupon_code} onChange={(v) => updateContent("coupon_code", v)} tag="p" className="font-bold text-xl text-emerald-700" placeholder="PROMO20" />
            </div>
            <div className="flex gap-3 justify-center mt-4">
              <button className="h-11 px-5 rounded-xl text-white text-sm font-medium" style={{ background: accent }}>
                <EditableText value={content.cta_checkout} onChange={(v) => updateContent("cta_checkout", v)} tag="span" className="text-white font-medium" placeholder="Checkout Sekarang" />
              </button>
            </div>
          </div>
          <div className="mt-4 flex gap-3 justify-center">
            <span className="text-white/60 text-xs">🔒 Pembayaran Aman • 🚚 Dikirim Seluruh Indonesia</span>
          </div>
        </div>
      </div>
    );
  }

  // ── DEFAULT/CUSTOM ──
  return (
    <div className={wrapperClass} onClick={onClick} style={{ background: bg }}>
      <TypeBadge />
      <Toolbar />
      <div className="p-8">
        <EditableText value={content.title} onChange={(v) => updateContent("title", v)} tag="h2" className="text-2xl font-bold text-stone-900" placeholder="Judul Section..." />
        <EditableText value={content.desc} onChange={(v) => updateContent("desc", v)} tag="p" className="text-stone-600 mt-2" placeholder="Isi konten section..." />
        {(section.image_source === "url" || section.image_source === "product") && (
          <EditableImage src={image} onChangeUrl={updateImage} className="aspect-video rounded-2xl overflow-hidden mt-4" alt={content.title || "Section"} />
        )}
      </div>
    </div>
  );
};

// ─── Main Page Component ─────────────────────────────────────────────────────
const AdminLandingPagesPage = () => {
  const [mode, setMode] = useState("list");
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [pages, setPages] = useState([]);
  const [step, setStep] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultPage);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [showSettings, setShowSettings] = useState(false);
  const [bottomBarOpen, setBottomBarOpen] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", onConfirm: null });
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const selectedProduct = useMemo(
    () => products.find((p) => String(p.id) === String(form.product_id)),
    [products, form.product_id]
  );
  const selectedTemplate = useMemo(
    () => templates.find((t) => String(t.id) === String(form.template_id)),
    [templates, form.template_id]
  );

  const loadAll = async () => {
    setLoading(true);
    try {
      const [productsData, templatesData, pagesData] = await Promise.all([
        productsApi.getAllAdmin(),
        landingPagesApi.getTemplatesAdmin(),
        landingPagesApi.getAllAdmin(),
      ]);
      setProducts(productsData || []);
      setTemplates(templatesData || []);
      setPages(pagesData || []);
    } catch (error) {
      toast.error(error.message || "Gagal memuat LP builder");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const startCreate = () => {
    setMode("builder");
    setStep(1);
    setEditingId(null);
    setSelectedIndex(null);
    setForm(defaultPage);
  };

  const applyProduct = (productId) => {
    const p = products.find((x) => String(x.id) === String(productId));
    if (!p) return;
    setForm((prev) => ({
      ...prev,
      product_id: productId,
      slug: prev.slug || slugify(p.slug || p.name),
      title: prev.title || p.name,
      description: prev.description || p.description || p.short_description || "",
      seo_title: prev.seo_title || `${p.name} - Promo Resmi`,
      seo_description: prev.seo_description || p.short_description || p.description || "",
      og_image: prev.og_image || p.image || "",
      cta_config: { ...prev.cta_config, whatsapp_text: `Halo saya tertarik dengan produk ${p.name}` },
    }));
  };

  const applyTemplate = () => {
    if (!selectedTemplate) return;
    const seeded = (selectedTemplate.template_sections || []).map((s, idx) => ({
      section_type: s.section_type,
      section_order: idx + 1,
      is_enabled: s.is_enabled,
      image_source: "product",
      image_url: "",
      content: {
        title: s.default_config?.title || sectionLabel(s.section_type),
        desc: s.default_config?.desc || selectedProduct?.short_description || "",
        ...s.default_config,
      },
      style: { accent: "#059669", bg: "#ffffff" },
    }));
    setForm((prev) => ({ ...prev, sections: seeded }));
    setSelectedIndex(null);
  };

  const editPage = (page) => {
    setMode("builder");
    setStep(3);
    setEditingId(page.id);
    setSelectedIndex(null);
    setForm({
      template_id: page.template_id,
      product_id: page.product_id || "",
      slug: page.slug || "",
      title: page.title || "",
      description: page.description || "",
      status: page.status || "draft",
      is_active: page.is_active ?? true,
      seo_title: page.seo_title || "",
      seo_description: page.seo_description || "",
      og_image: page.og_image || "",
      cta_config: page.cta_config || defaultPage.cta_config,
      sections: (page.sections || []).map(toUiSection).sort((a, b) => a.section_order - b.section_order),
    });
  };

  const togglePageActive = async (page) => {
    try {
      await landingPagesApi.updateStatus(page.id, !page.is_active);
      toast.success(page.is_active ? "Landing page dinonaktifkan" : "Landing page diaktifkan");
      await loadAll();
    } catch (error) {
      toast.error(error.message || "Gagal mengubah status");
    }
  };

  const deletePage = async (page) => {
    setConfirmDialog({
      open: true,
      title: "Hapus Landing Page",
      message: `Hapus "${page.title}"? Aksi ini tidak bisa dibatalkan dan semua data section akan ikut terhapus.`,
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
        try {
          await landingPagesApi.delete(page.id);
          toast.success("Landing page dihapus");
          await loadAll();
        } catch (error) {
          toast.error(error.message || "Gagal menghapus landing page");
        }
      },
    });
  };

  const normalizeOrders = (sections) => sections.map((s, i) => ({ ...s, section_order: i + 1 }));

  const updateSection = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));
  };

  const moveSection = (index, dir) => {
    setForm((prev) => {
      const list = [...prev.sections];
      const target = dir === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= list.length) return prev;
      [list[index], list[target]] = [list[target], list[index]];
      setSelectedIndex(target);
      return { ...prev, sections: normalizeOrders(list) };
    });
  };

  const duplicateSection = (index) => {
    setForm((prev) => {
      const list = [...prev.sections];
      list.splice(index + 1, 0, { ...JSON.parse(JSON.stringify(list[index])) });
      return { ...prev, sections: normalizeOrders(list) };
    });
  };

  const deleteSection = (index) => {
    setConfirmDialog({
      open: true,
      title: "Hapus Section",
      message: `Hapus section "${sectionLabel(form.sections[index]?.section_type)}"? Section ini akan hilang dari landing page.`,
      onConfirm: () => {
        setForm((prev) => {
          const list = prev.sections.filter((_, i) => i !== index);
          if (selectedIndex === index) setSelectedIndex(null);
          else if (selectedIndex > index) setSelectedIndex(selectedIndex - 1);
          return { ...prev, sections: normalizeOrders(list) };
        });
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
      },
    });
  };

  const addSection = (type = "custom") => {
    const newSection = {
      section_type: type,
      section_order: form.sections.length + 1,
      is_enabled: true,
      image_source: "product",
      image_url: "",
      content: {
        title: "",
        desc: "",
        items: type === "faq" ? [{ q: "", a: "" }, { q: "", a: "" }]
          : type === "testimonial" ? [{ name: "", text: "", role: "", image_url: "" }, { name: "", text: "", role: "", image_url: "" }, { name: "", text: "", role: "", image_url: "" }]
          : type === "problem" ? [{ title: "", desc: "", icon: "😩" }, { title: "", desc: "", icon: "🍔" }, { title: "", desc: "", icon: "🥤" }]
          : type === "how_to_use" ? [{ title: "", desc: "" }, { title: "", desc: "" }, { title: "", desc: "" }]
          : type === "benefit" ? [{ icon: "✨", title: "", desc: "" }, { icon: "❤️", title: "", desc: "" }, { icon: "🛡️", title: "", desc: "" }]
          : type === "stats" ? [{ number: "", label: "" }, { number: "", label: "" }, { number: "", label: "" }, { number: "", label: "" }]
          : type === "guarantee" ? [{ icon: "🔒", title: "", desc: "" }, { icon: "🚚", title: "", desc: "" }, { icon: "📦", title: "", desc: "" }]
          : undefined,
        bullets: type === "hero" ? ["", "", ""] : undefined,
        points: type === "solution" ? ["", "", ""] : type === "hard_sell" ? ["", "", "", ""] : undefined,
        bonus_items: type === "offer" ? ["", "", ""] : undefined,
        coupon_code: type === "offer" || type === "final_cta" ? "" : undefined,
        coupon_label: type === "offer" || type === "final_cta" ? "" : undefined,
        badge: type === "offer" || type === "final_cta" ? "" : undefined,
        remaining: type === "urgency" ? 27 : undefined,
        total: type === "urgency" ? 100 : undefined,
        cta_text: type === "hard_sell" ? "" : undefined,
        cta_checkout: type === "final_cta" ? "" : undefined,
        cta_whatsapp: type === "final_cta" ? "" : undefined,
        tip: type === "how_to_use" ? "" : undefined,
      },
      style: { accent: "#059669", bg: "#ffffff" },
    };
    setForm((prev) => ({ ...prev, sections: [...prev.sections, newSection] }));
    setSelectedIndex(form.sections.length);
  };

  const savePage = async (nextStatus = null) => {
    try {
      const payload = {
        ...form,
        template_id: Number(form.template_id),
        product_id: form.product_id ? Number(form.product_id) : null,
        slug: slugify(form.slug || form.title || selectedProduct?.name || ""),
        title: form.title || selectedProduct?.name || "Landing Page",
        status: nextStatus || form.status,
        sections: normalizeOrders(form.sections).map(toApiSection),
      };
      if (editingId) {
        await landingPagesApi.update(editingId, payload);
      } else {
        await landingPagesApi.create(payload);
      }
      toast.success(nextStatus === "published" ? "Landing page published!" : "Landing page disimpan");
      await loadAll();
      setMode("list");
    } catch (error) {
      toast.error(error.message || "Gagal simpan landing page");
    }
  };

  // ─── LIST MODE ─────────────────────────────────────────────────────────────
  if (loading) return <AdminLayout><div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div></AdminLayout>;

  if (mode === "list") {
    return (
      <AdminLayout>
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-stone-900">Landing Page Builder</h1>
              <p className="text-stone-500 mt-1">Buat dan kelola landing page produk</p>
            </div>
            <button onClick={startCreate} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 h-12 text-sm font-medium text-white hover:bg-emerald-700 transition-colors">
              <Plus className="h-4 w-4" /> Buat LP Baru
            </button>
          </div>

          {pages.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
              <Layout className="w-12 h-12 mx-auto text-stone-300" />
              <p className="mt-3 text-stone-500">Belum ada landing page</p>
              <button onClick={startCreate} className="mt-4 text-sm text-emerald-600 font-medium hover:underline">Buat landing page pertama</button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {pages.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  {p.og_image && <div className="aspect-video bg-stone-100"><img src={p.og_image} alt={p.title} className="w-full h-full object-cover" /></div>}
                  <div className="p-4">
                    <h3 className="font-semibold text-stone-900 truncate">{p.title}</h3>
                    <p className="text-sm text-stone-500 mt-1">/lp/{p.slug}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : p.status === "archived" ? "bg-stone-100 text-stone-600" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.is_active ? "bg-blue-100 text-blue-700" : "bg-stone-100 text-stone-500"}`}>{p.is_active ? "Active" : "Inactive"}</span>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => editPage(p)} className="flex-1 h-9 rounded-lg border border-stone-200 text-sm font-medium hover:bg-stone-50 transition-colors flex items-center justify-center gap-1">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <a href={`/lp/${p.slug}`} target="_blank" rel="noreferrer" className="flex-1 h-9 rounded-lg border border-emerald-200 text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors flex items-center justify-center gap-1">
                        <Eye className="w-3.5 h-3.5" /> Preview
                      </a>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => togglePageActive(p)}
                        className={`flex-1 h-9 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-1 ${p.is_active ? "border-amber-200 text-amber-700 hover:bg-amber-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}
                      >
                        {p.is_active ? <><EyeOff className="w-3.5 h-3.5" /> Nonaktifkan</> : <><Eye className="w-3.5 h-3.5" /> Aktifkan</>}
                      </button>
                      <button
                        onClick={() => deletePage(p)}
                        className="flex-1 h-9 rounded-lg border border-rose-200 text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors flex items-center justify-center gap-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Hapus
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
        />
      </AdminLayout>
    );
  }

  // ─── BUILDER MODE ──────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <main className="h-[calc(100vh-4rem)] flex flex-col overflow-hidden bg-stone-100">
        {/* Top toolbar */}
        <div className="flex items-center justify-between border-b border-stone-200 bg-white px-4 py-2.5 shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setMode("list")} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><X className="w-5 h-5" /></button>
            <span className="text-sm font-medium text-stone-700">
              {step < 3 ? `Step ${step}/2 — ${step === 1 ? "Pilih Produk" : "Pilih Template"}` : (form.title || "LP Builder")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {step === 3 && (
              <>
                <div className="inline-flex rounded-lg border border-stone-200 p-0.5 mr-2">
                  <button onClick={() => setPreviewDevice("desktop")} className={`inline-flex items-center rounded-md px-2.5 py-1.5 text-xs font-medium ${previewDevice === "desktop" ? "bg-emerald-600 text-white" : "text-stone-600 hover:bg-stone-50"}`}>
                    <Monitor className="mr-1 h-3.5 w-3.5" /> Desktop
                  </button>
                  <button onClick={() => setPreviewDevice("mobile")} className={`inline-flex items-center rounded-md px-2.5 py-1.5 text-xs font-medium ${previewDevice === "mobile" ? "bg-emerald-600 text-white" : "text-stone-600 hover:bg-stone-50"}`}>
                    <Smartphone className="mr-1 h-3.5 w-3.5" /> Mobile
                  </button>
                </div>
                <button onClick={() => setShowSettings(!showSettings)} className={`p-2 rounded-lg border ${showSettings ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                  <Settings className="w-4 h-4" />
                </button>
                <button onClick={() => setBottomBarOpen(!bottomBarOpen)} className={`p-2 rounded-lg border ${bottomBarOpen ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`} title="Layers">
                  <Layers className="w-4 h-4" />
                </button>
                <a href={`/lp/${slugify(form.slug || form.title || "preview")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-stone-200 px-3 py-2 text-sm hover:bg-stone-50"><Eye className="h-4 w-4" /> Preview</a>
                <button onClick={() => savePage("draft")} className="inline-flex items-center gap-1 rounded-lg border border-stone-200 px-3 py-2 text-sm hover:bg-stone-50"><Save className="h-4 w-4" /> Simpan</button>
                <button onClick={() => savePage("published")} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">Publish</button>
              </>
            )}
          </div>
        </div>

        {/* Step 1 & 2: Selection */}
        {step < 3 && (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="mx-auto max-w-4xl">
              {step === 1 && (
                <div className="bg-white rounded-2xl border border-stone-200 p-6">
                  <h2 className="text-xl font-semibold text-stone-900">Pilih Produk</h2>
                  <p className="text-stone-500 mt-1">Produk mana yang akan dibuatkan landing page?</p>
                  <div className="mt-5 grid gap-3 md:grid-cols-2">
                    {products.map((p) => (
                      <button key={p.id} onClick={() => applyProduct(p.id)} className={`flex items-start gap-4 rounded-xl border p-4 text-left transition-all ${String(form.product_id) === String(p.id) ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100" : "border-stone-200 hover:border-emerald-300"}`}>
                        {p.image && <img src={p.image} alt={p.name} className="w-16 h-16 rounded-xl object-cover shrink-0" />}
                        <div>
                          <p className="font-semibold text-stone-900">{p.name}</p>
                          <p className="mt-0.5 text-xs text-stone-500 line-clamp-2">{p.short_description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button onClick={() => setStep(2)} disabled={!form.product_id} className="rounded-xl bg-emerald-600 px-5 h-12 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">Lanjut Pilih Template →</button>
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="bg-white rounded-2xl border border-stone-200 p-6">
                  <h2 className="text-xl font-semibold text-stone-900">Pilih Template</h2>
                  <p className="text-stone-500 mt-1">Template akan langsung tampil dan bisa diedit visual</p>
                  <div className="mt-5 grid gap-3 md:grid-cols-2">
                    {templates.map((t) => (
                      <button key={t.id} onClick={() => setForm((prev) => ({ ...prev, template_id: t.id }))} className={`rounded-xl border p-4 text-left transition-all ${String(form.template_id) === String(t.id) ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100" : "border-stone-200 hover:border-emerald-300"}`}>
                        <p className="font-semibold text-stone-900">{t.name}</p>
                        <p className="mt-1 text-xs text-stone-500">{t.description}</p>
                        <p className="mt-2 text-xs text-emerald-600">{(t.template_sections || []).length} sections</p>
                      </button>
                    ))}
                  </div>
                  <div className="mt-6 flex justify-between">
                    <button onClick={() => setStep(1)} className="rounded-xl border border-stone-200 px-5 h-12 text-sm font-medium hover:bg-stone-50">← Kembali</button>
                    <button onClick={() => { applyTemplate(); setStep(3); }} disabled={!form.template_id} className="rounded-xl bg-emerald-600 px-5 h-12 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">Mulai Edit Visual →</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Visual Builder */}
        {step === 3 && (
          <div className="flex-1 flex overflow-hidden relative">
            {/* Settings panel (left, toggle) */}
            {showSettings && (
              <aside className="w-72 border-r border-stone-200 bg-white overflow-y-auto shrink-0 p-4">
                <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Pengaturan LP
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-stone-600">Judul</label>
                    <input className="w-full mt-1 h-9 px-3 rounded-lg border border-stone-200 text-sm focus:ring-2 focus:ring-emerald-500" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-600">Slug</label>
                    <input className="w-full mt-1 h-9 px-3 rounded-lg border border-stone-200 text-sm focus:ring-2 focus:ring-emerald-500" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-600">SEO Title</label>
                    <input className="w-full mt-1 h-9 px-3 rounded-lg border border-stone-200 text-sm" value={form.seo_title} onChange={(e) => setForm((p) => ({ ...p, seo_title: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-600">SEO Description</label>
                    <textarea className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-200 text-sm" rows={3} value={form.seo_description} onChange={(e) => setForm((p) => ({ ...p, seo_description: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-600">CTA Mode</label>
                    <select className="w-full mt-1 h-9 px-3 rounded-lg border border-stone-200 text-sm" value={form.cta_config.mode} onChange={(e) => setForm((p) => ({ ...p, cta_config: { ...p.cta_config, mode: e.target.value } }))}>
                      <option value="both">Checkout + WhatsApp</option>
                      <option value="web">Checkout saja</option>
                      <option value="whatsapp">WhatsApp saja</option>
                      <option value="custom_url">Custom Link</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-600">WhatsApp Text</label>
                    <textarea className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-200 text-sm" rows={2} value={form.cta_config.whatsapp_text} onChange={(e) => setForm((p) => ({ ...p, cta_config: { ...p.cta_config, whatsapp_text: e.target.value } }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-600">Meta Pixel ID</label>
                    <input className="w-full mt-1 h-9 px-3 rounded-lg border border-stone-200 text-sm focus:ring-2 focus:ring-emerald-500" value={form.cta_config.meta_pixel_id || ""} onChange={(e) => setForm((p) => ({ ...p, cta_config: { ...p.cta_config, meta_pixel_id: e.target.value.trim() } }))} placeholder="Contoh: 123456789012345" />
                    <p className="text-xs text-stone-400 mt-1">Pixel ID dari Meta Business Suite. Kosongkan untuk pakai global.</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-600">Status</label>
                    <select className="w-full mt-1 h-9 px-3 rounded-lg border border-stone-200 text-sm" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </aside>
            )}

            {/* Visual Canvas */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className={`mx-auto space-y-4 ${previewDevice === "mobile" ? "max-w-sm" : "max-w-5xl"}`}>
                {form.sections.map((s, idx) => (
                  <SectionRenderer
                    key={`${s.section_type}-${idx}`}
                    section={s}
                    product={selectedProduct}
                    isActive={selectedIndex === idx}
                    onClick={() => setSelectedIndex(idx)}
                    onUpdate={(patch) => updateSection(idx, patch)}
                    onDelete={() => deleteSection(idx)}
                    onMoveUp={() => moveSection(idx, "up")}
                    onMoveDown={() => moveSection(idx, "down")}
                    onDuplicate={() => duplicateSection(idx)}
                    onToggle={() => updateSection(idx, { is_enabled: !s.is_enabled })}
                  />
                ))}

                {/* Add section */}
                <div className="border-2 border-dashed border-stone-300 rounded-2xl p-6 text-center">
                  <p className="text-sm text-stone-500 mb-3">Tambah Section</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {Object.entries(SECTION_TYPES).map(([key, cfg]) => (
                      <button
                        key={key}
                        onClick={() => addSection(key)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 bg-white text-xs font-medium text-stone-700 hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
                      >
                        <span>{cfg.icon}</span> {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Layers Panel (right side, Canva-style) ─── */}
            {bottomBarOpen && (
              <aside className="w-64 border-l border-stone-200 bg-white shrink-0 flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
                  <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
                    <Layers className="w-4 h-4" /> Layers
                  </h3>
                  <button onClick={() => setBottomBarOpen(false)} className="p-1 rounded-md hover:bg-stone-100 text-stone-400">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {/* Section list with drag & drop */}
                <div className="flex-1 overflow-y-auto py-1">
                  {form.sections.map((s, idx) => (
                    <div
                      key={`layer-${idx}`}
                      draggable
                      onDragStart={() => setDragIndex(idx)}
                      onDragOver={(e) => { e.preventDefault(); setDragOverIndex(idx); }}
                      onDragLeave={() => setDragOverIndex(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragIndex !== null && dragIndex !== idx) {
                          setForm((prev) => {
                            const list = [...prev.sections];
                            const [moved] = list.splice(dragIndex, 1);
                            list.splice(idx, 0, moved);
                            setSelectedIndex(idx);
                            return { ...prev, sections: normalizeOrders(list) };
                          });
                        }
                        setDragIndex(null);
                        setDragOverIndex(null);
                      }}
                      onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                      onClick={() => setSelectedIndex(idx)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all border-l-2 cursor-grab active:cursor-grabbing select-none ${
                        dragOverIndex === idx && dragIndex !== idx
                          ? "border-l-emerald-500 bg-emerald-100"
                          : selectedIndex === idx
                            ? "border-l-emerald-500 bg-emerald-50"
                            : "border-l-transparent hover:bg-stone-50"
                      } ${dragIndex === idx ? "opacity-40" : ""} ${!s.is_enabled ? "opacity-40" : ""}`}
                    >
                      <GripVertical className="w-3.5 h-3.5 text-stone-300 shrink-0" />
                      <span className="text-sm shrink-0">{sectionIcon(s.section_type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-medium truncate ${selectedIndex === idx ? "text-emerald-800" : "text-stone-700"}`}>
                          {sectionLabel(s.section_type)}
                        </p>
                        {s.content?.title && (
                          <p className="text-[10px] text-stone-400 truncate">{s.content.title}</p>
                        )}
                      </div>
                      {!s.is_enabled && <EyeOff className="w-3 h-3 text-stone-400 shrink-0" />}
                    </div>
                  ))}
                </div>
                {/* Footer info */}
                <div className="px-4 py-2.5 border-t border-stone-100 text-[10px] text-stone-400">
                  {form.sections.length} sections
                  {form.sections.filter(s => !s.is_enabled).length > 0 && ` • ${form.sections.filter(s => !s.is_enabled).length} hidden`}
                </div>
              </aside>
            )}
          </div>
        )}
      </main>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
      />
    </AdminLayout>
  );
};

export default AdminLandingPagesPage;
